import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { KeyRound } from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'
import {
  AppSession,
  DEFAULT_LOCAL_PASSWORD,
  RememberedAccess,
  clearSession,
  createLocalCredentials,
  getLocalAdminSession,
  normalizeEmail,
  readLocalPasswordOverride,
  readRememberedAccess,
  readSession,
  saveLocalPasswordOverride,
  saveSession,
  verifyLocalPassword,
} from '../lib/appSession'

const APP_VERSION = '1.0.5'
const SUPPORT_URL = 'https://calendly.com/suporter3/agenda'
const LOGIN_BACKGROUND_URL = 'https://static.lumi.new/material/68/68e17c674fdeea8607d591abe2406e13.jpg'
const LOGIN_LOOKUP_BATCH_SIZE = 100

interface ResearcherRecord {
  _id?: string
  id?: string
  name?: string
  email?: string
  isActive?: boolean
  authProvider?: 'local' | 'google'
  googleLinked?: boolean
  passwordHash?: string
  passwordSalt?: string
  mustResetPassword?: boolean
  passwordUpdatedAt?: string
}

interface AdminRecord {
  _id?: string
  id?: string
  name?: string
  email?: string
  isActive?: boolean
  role?: string
  authProvider?: 'local' | 'google'
  googleLinked?: boolean
  passwordHash?: string
  passwordSalt?: string
  mustResetPassword?: boolean
  passwordUpdatedAt?: string
  createdAt?: string
  updatedAt?: string
  recordVersion?: number
  adminRootId?: string
  supersedesAdminId?: string
  lifecycleAction?: 'create' | 'update' | 'status' | 'reset_password' | 'delete' | 'migrate'
  isDeleted?: boolean
  deletedAt?: string
}

interface PendingPasswordReset {
  record: ResearcherRecord | AdminRecord
  recordId: string
  entityName: 'researchers' | 'admins'
  session: AppSession
}

const getRecordId = (record: ResearcherRecord | AdminRecord) => record._id ?? record.id

const isPermissionError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error)
  return message.includes('Insufficient permissions') || message.includes('OPERATION_FAILED')
}

const mergeRecordUpdate = <T extends ResearcherRecord | AdminRecord>(original: T, updated: T): T => ({
  ...original,
  ...updated,
  _id: updated._id ?? original._id,
  id: updated.id ?? original.id,
})

const getAdminRecordTime = (admin: AdminRecord) => {
  const candidates = [admin.deletedAt, admin.updatedAt, admin.passwordUpdatedAt, admin.createdAt]
  return candidates.reduce((latest, value) => {
    const timestamp = value ? new Date(value).getTime() : 0
    return Number.isFinite(timestamp) ? Math.max(latest, timestamp) : latest
  }, 0)
}

const resolveEffectiveAdmin = (records: AdminRecord[], email: string): AdminRecord | null => {
  const normalizedEmail = normalizeEmail(email)
  const grouped = new Map<string, AdminRecord[]>()

  records.forEach((admin) => {
    const recordId = getRecordId(admin)
    const emailKey = normalizeEmail(admin.email || '')
    const key = admin.adminRootId || recordId || emailKey
    if (!key) return
    grouped.set(key, [...(grouped.get(key) || []), admin])
  })

  const effectiveRecords = Array.from(grouped.values()).map((versions) => (
    [...versions].sort((left, right) => {
      const versionDifference = Number(right.recordVersion || 1) - Number(left.recordVersion || 1)
      if (versionDifference !== 0) return versionDifference
      return getAdminRecordTime(right) - getAdminRecordTime(left)
    })[0]
  ))

  const effectiveAdmin = effectiveRecords.find((admin) => (
    normalizeEmail(admin.email || '') === normalizedEmail
  ))
  return effectiveAdmin && effectiveAdmin.isDeleted !== true ? effectiveAdmin : null
}

const createAdminReplacementRecord = async (
  admin: AdminRecord,
  updates: Partial<AdminRecord>,
  lifecycleAction: NonNullable<AdminRecord['lifecycleAction']>
): Promise<AdminRecord> => {
  const now = new Date().toISOString()
  const previousId = getRecordId(admin)

  return await lumi.entities.admins.create({
    name: updates.name ?? admin.name ?? '',
    email: normalizeEmail(updates.email ?? admin.email ?? ''),
    isActive: updates.isActive ?? admin.isActive ?? true,
    role: updates.role ?? admin.role ?? 'admin',
    authProvider: updates.authProvider ?? admin.authProvider ?? 'local',
    googleLinked: updates.googleLinked ?? admin.googleLinked ?? false,
    passwordHash: updates.passwordHash ?? admin.passwordHash,
    passwordSalt: updates.passwordSalt ?? admin.passwordSalt,
    mustResetPassword: updates.mustResetPassword ?? admin.mustResetPassword ?? false,
    passwordUpdatedAt: updates.passwordUpdatedAt ?? admin.passwordUpdatedAt,
    recordVersion: Number(admin.recordVersion || 1) + 1,
    adminRootId: admin.adminRootId || previousId || undefined,
    supersedesAdminId: previousId || undefined,
    lifecycleAction,
    isDeleted: false,
    createdAt: now,
    updatedAt: now,
  }) as AdminRecord
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [rememberDevice, setRememberDevice] = useState(true)
  const [savedAccess, setSavedAccess] = useState<RememberedAccess | null>(() => readRememberedAccess())
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [pendingReset, setPendingReset] = useState<PendingPasswordReset | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [savingNewPassword, setSavingNewPassword] = useState(false)

  useEffect(() => {
    const session = readSession()
    const remembered = readRememberedAccess()

    if (remembered?.email) {
      setEmail(remembered.email)
    }

    setSavedAccess(remembered)

    if (session) {
      navigate(session.profile === 'admin' ? '/admin' : '/researcher', { replace: true })
    }
  }, [navigate])

  const findResearcherByEmail = async (inputEmail: string) => {
    const normalizedInputEmail = normalizeEmail(inputEmail)

    try {
      const response = await lumi.entities.researchers.list({
        filter: { email: inputEmail.trim() },
        limit: 1,
      }) as { list?: ResearcherRecord[] }

      const directMatch = response.list?.[0]
      if (directMatch) return directMatch

      // Varredura de segurança em lotes pequenos, para não carregar a coleção inteira de uma vez.
      for (let skip = 0; ; skip += LOGIN_LOOKUP_BATCH_SIZE) {
        const batch = await lumi.entities.researchers.list({
          limit: LOGIN_LOOKUP_BATCH_SIZE,
          skip,
        }) as { list?: ResearcherRecord[] }

        const batchList = batch.list || []
        const match = batchList.find((item) => (
          normalizeEmail(item.email || '') === normalizedInputEmail
        ))

        if (match) return match
        if (batchList.length < LOGIN_LOOKUP_BATCH_SIZE) return null
      }
    } catch (error) {
      console.warn('Não foi possível localizar pesquisador por email:', error)
      return null
    }
  }

  const findAdminByEmail = async (inputEmail: string) => {
    const normalizedInputEmail = normalizeEmail(inputEmail)

    try {
      const admins: AdminRecord[] = []

      for (let skip = 0; ; skip += LOGIN_LOOKUP_BATCH_SIZE) {
        const batch = await lumi.entities.admins.list({
          limit: LOGIN_LOOKUP_BATCH_SIZE,
          skip,
        }) as { list?: AdminRecord[] }

        const batchList = batch.list || []
        admins.push(...batchList)

        if (batchList.length < LOGIN_LOOKUP_BATCH_SIZE) break
      }

      return resolveEffectiveAdmin(admins, normalizedInputEmail)
    } catch (error) {
      console.warn('Não foi possível localizar administrador por email:', error)
      return null
    }
  }

  const ensureAdminLocalAccess = async (
    admin: AdminRecord,
    inputPassword: string
  ): Promise<AdminRecord | null> => {
    const adminId = getRecordId(admin)
    const localOverride = readLocalPasswordOverride('admin', admin.email || '')

    // Credencial local de contingência tem prioridade (mesmo fluxo do pesquisador).
    if (localOverride && await verifyLocalPassword(inputPassword, localOverride)) {
      return {
        ...admin,
        ...localOverride,
        authProvider: 'local',
        googleLinked: false,
        mustResetPassword: false,
      }
    }

    const hasPassword = !!admin.passwordHash && !!admin.passwordSalt

    if (hasPassword) {
      const passwordMatches = await verifyLocalPassword(inputPassword, admin)
      if (!passwordMatches) return null

      if (admin.authProvider === 'local' && admin.googleLinked === false) {
        return admin
      }

      if (!adminId) return admin

      try {
        const updatedAdmin = await lumi.entities.admins.update(adminId, {
          authProvider: 'local',
          googleLinked: false,
          updatedAt: new Date().toISOString(),
        }) as AdminRecord

        return mergeRecordUpdate(admin, updatedAdmin)
      } catch (error) {
        if (!isPermissionError(error)) throw error
        console.warn('[APP DNA] Sem permissão para atualizar vínculo local do administrador. Criando versão persistente.', error)
        return await createAdminReplacementRecord(admin, {
          authProvider: 'local',
          googleLinked: false,
        }, 'migrate')
      }
    }

    if (inputPassword !== DEFAULT_LOCAL_PASSWORD) return null

    const credentials = await createLocalCredentials(DEFAULT_LOCAL_PASSWORD, {
      mustResetPassword: true,
    })

    if (!adminId) {
      return {
        ...admin,
        ...credentials,
      }
    }

    try {
      const updatedAdmin = await lumi.entities.admins.update(adminId, {
        ...credentials,
        updatedAt: new Date().toISOString(),
      }) as AdminRecord

      return mergeRecordUpdate(admin, updatedAdmin)
    } catch (error) {
      if (!isPermissionError(error)) throw error
      console.warn('[APP DNA] Sem permissão para criar senha padrão no administrador. Criando versão persistente.', error)
      return await createAdminReplacementRecord(admin, credentials, 'migrate')
    }
  }

  const ensureResearcherLocalAccess = async (
    researcher: ResearcherRecord,
    inputPassword: string
  ): Promise<ResearcherRecord | null> => {
    const researcherId = getRecordId(researcher)
    const localOverride = readLocalPasswordOverride('researcher', researcher.email || '')

    if (localOverride && await verifyLocalPassword(inputPassword, localOverride)) {
      return {
        ...researcher,
        ...localOverride,
        authProvider: 'local',
        googleLinked: false,
        mustResetPassword: false,
      }
    }

    const hasPassword = !!researcher.passwordHash && !!researcher.passwordSalt

    if (hasPassword) {
      const passwordMatches = await verifyLocalPassword(inputPassword, researcher)
      if (!passwordMatches) return null

      if (researcher.authProvider === 'local' && researcher.googleLinked === false) {
        return researcher
      }

      if (!researcherId) return researcher

      try {
        const updatedResearcher = await lumi.entities.researchers.update(researcherId, {
          authProvider: 'local',
          googleLinked: false,
          updatedAt: new Date().toISOString(),
        }) as ResearcherRecord

        return mergeRecordUpdate(researcher, updatedResearcher)
      } catch (error) {
        if (!isPermissionError(error)) throw error
        console.warn('[APP DNA] Sem permissão para atualizar vínculo local do pesquisador. Login continuará sem persistir esta normalização.', error)
        return {
          ...researcher,
          authProvider: 'local',
          googleLinked: false,
        }
      }
    }

    if (inputPassword !== DEFAULT_LOCAL_PASSWORD) return null

    const credentials = await createLocalCredentials(DEFAULT_LOCAL_PASSWORD, {
      mustResetPassword: true,
    })

    if (!researcherId) {
      return {
        ...researcher,
        ...credentials,
      }
    }

    try {
      const updatedResearcher = await lumi.entities.researchers.update(researcherId, {
        ...credentials,
        updatedAt: new Date().toISOString(),
      }) as ResearcherRecord

      return mergeRecordUpdate(researcher, updatedResearcher)
    } catch (error) {
      if (!isPermissionError(error)) throw error
      console.warn('[APP DNA] Sem permissão para criar senha padrão no pesquisador. Fluxo seguirá com senha temporária local.', error)
      return {
        ...researcher,
        ...credentials,
      }
    }
  }

  const buildAdminSession = (admin: AdminRecord): AppSession | null => {
    const adminId = getRecordId(admin)
    const normalizedAdminEmail = normalizeEmail(admin.email || '')

    if (!adminId || !normalizedAdminEmail || !admin.name) return null

    return {
      profile: 'admin',
      userId: adminId,
      email: normalizedAdminEmail,
      userName: admin.name,
      userRole: 'admin',
      researcherId: null,
      researcherName: null,
      loginAt: new Date().toISOString(),
    }
  }

  const buildResearcherSession = (researcher: ResearcherRecord): AppSession | null => {
    const researcherId = getRecordId(researcher)
    const normalizedResearcherEmail = normalizeEmail(researcher.email || '')

    if (!researcherId || !normalizedResearcherEmail || !researcher.name) return null

    return {
      profile: 'researcher',
      userId: researcherId,
      email: normalizedResearcherEmail,
      userName: researcher.name,
      userRole: 'researcher',
      researcherId,
      researcherName: researcher.name,
      loginAt: new Date().toISOString(),
    }
  }

  const finishLogin = (session: AppSession) => {
    saveSession(session, rememberDevice)
    setSavedAccess(readRememberedAccess())
    toast.success(`Bem-vindo, ${session.userName}!`)
    navigate(session.profile === 'admin' ? '/admin' : '/researcher', { replace: true })
  }

  const savePasswordResetCredentials = async (
    pendingResetData: PendingPasswordReset,
    recordId: string,
    credentials: Awaited<ReturnType<typeof createLocalCredentials>>
  ): Promise<{ mode: 'remote' | 'local'; recordId: string }> => {
    const idAsString = String(recordId).trim()

    if (!idAsString) {
      throw new Error('ID do registro inválido para redefinição de senha.')
    }

    const payload = {
      ...credentials,
      mustResetPassword: false,
      updatedAt: new Date().toISOString(),
    }

    console.info('[APP DNA] savePasswordResetCredentials — entidade:', pendingResetData.entityName, '| id:', idAsString)

    if (pendingResetData.entityName === 'admins') {
      try {
        const result = await lumi.entities.admins.update(idAsString, payload)
        console.info('[APP DNA] admins.update result:', result)
        return { mode: 'remote', recordId: getRecordId(result as AdminRecord) || idAsString }
      } catch (error) {
        if (!isPermissionError(error)) throw error
        console.warn('[APP DNA] Sem permissão para salvar senha do administrador. Criando versão persistente.', error)

        try {
          const replacement = await createAdminReplacementRecord(
            pendingResetData.record as AdminRecord,
            payload,
            'reset_password'
          )
          return { mode: 'remote', recordId: getRecordId(replacement) || idAsString }
        } catch (replacementError) {
          console.warn('[APP DNA] Não foi possível criar versão persistente. Salvando credencial local de contingência.', replacementError)
          saveLocalPasswordOverride('admin', pendingResetData.session.email, credentials)
          return { mode: 'local', recordId: idAsString }
        }
      }
    }

    try {
      const result = await lumi.entities.researchers.update(idAsString, payload)
      console.info('[APP DNA] researchers.update result:', result)
      return { mode: 'remote', recordId: getRecordId(result as ResearcherRecord) || idAsString }
    } catch (error) {
      if (!isPermissionError(error)) throw error
      console.warn('[APP DNA] Sem permissão para salvar senha do pesquisador. Salvando credencial local de contingência.', error)
      saveLocalPasswordOverride('researcher', pendingResetData.session.email, credentials)
      return { mode: 'local', recordId: idAsString }
    }
  }

  const handleLogin = async () => {
    const normalizedInputEmail = normalizeEmail(email)

    if (!normalizedInputEmail || !password) {
      toast.error('Informe e-mail e senha.')
      return
    }

    setLoading(true)

    try {
      clearSession()

      const adminSession = await getLocalAdminSession(normalizedInputEmail, password)
      if (adminSession) {
        finishLogin(adminSession)
        return
      }

      const admin = await findAdminByEmail(normalizedInputEmail)

      if (admin) {
        if (admin.isActive === false) {
          toast.error('Este administrador está inativo.')
          return
        }

        const adminWithAccess = await ensureAdminLocalAccess(admin, password)

        if (!adminWithAccess) {
          toast.error('E-mail ou senha inválido.')
          return
        }

        const registeredAdminSession = buildAdminSession(adminWithAccess)

        if (!registeredAdminSession) {
          toast.error('Cadastro de administrador incompleto. Revise nome e e-mail no painel.')
          return
        }

        if (adminWithAccess.mustResetPassword || password === DEFAULT_LOCAL_PASSWORD) {
          const adminId = getRecordId(adminWithAccess) || getRecordId(admin)

          if (!adminId) {
            toast.error('Não foi possível identificar o administrador. Revise o cadastro.')
            return
          }

          setPendingReset({
            record: adminWithAccess,
            recordId: adminId,
            entityName: 'admins',
            session: registeredAdminSession,
          })
          setNewPassword('')
          setConfirmPassword('')
          toast('Defina uma nova senha para concluir o acesso.')
          return
        }

        finishLogin(registeredAdminSession)
        return
      }

      const researcher = await findResearcherByEmail(normalizedInputEmail)

      if (!researcher) {
        toast.error('E-mail ou senha inválido.')
        return
      }

      if (researcher.isActive === false) {
        toast.error('Este pesquisador está inativo. Solicite liberação ao administrador.')
        return
      }

      const researcherWithAccess = await ensureResearcherLocalAccess(researcher, password)

      if (!researcherWithAccess) {
        toast.error('E-mail ou senha inválido.')
        return
      }

      const session = buildResearcherSession(researcherWithAccess)

      if (!session) {
        toast.error('Cadastro de pesquisador incompleto. Revise nome e e-mail no painel.')
        return
      }

      if (researcherWithAccess.mustResetPassword || password === DEFAULT_LOCAL_PASSWORD) {
        const researcherId = getRecordId(researcherWithAccess) || getRecordId(researcher)

        if (!researcherId) {
          toast.error('Não foi possível identificar o pesquisador. Revise o cadastro.')
          return
        }

        setPendingReset({
          record: researcherWithAccess,
          recordId: researcherId,
          entityName: 'researchers',
          session,
        })
        setNewPassword('')
        setConfirmPassword('')
        toast('Defina uma nova senha para concluir o acesso.')
        return
      }

      finishLogin(session)
    } catch (error) {
      console.error('Erro ao fazer login local:', error)
      toast.error('Erro ao fazer login')
    } finally {
      setLoading(false)
    }
  }

  const handleCompletePasswordReset = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!pendingReset) return

    if (newPassword.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres.')
      return
    }

    if (newPassword === DEFAULT_LOCAL_PASSWORD) {
      toast.error('Escolha uma senha diferente da senha padrão.')
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error('A confirmação da senha não confere.')
      return
    }

    const rawId = pendingReset.recordId || getRecordId(pendingReset.record) || pendingReset.session.userId
    const recordId = rawId ? String(rawId).trim() : ''

    if (!recordId) {
      toast.error('Não foi possível identificar o usuário. Verifique o cadastro.')
      console.error('[APP DNA] handleCompletePasswordReset — record sem ID:', pendingReset.record)
      return
    }

    try {
      setSavingNewPassword(true)

      const credentials = await createLocalCredentials(newPassword, {
        mustResetPassword: false,
      })

      const saveResult = await savePasswordResetCredentials(pendingReset, recordId, credentials)

      const completedSession: AppSession = {
        ...pendingReset.session,
        userId: saveResult.recordId,
        loginAt: new Date().toISOString(),
      }

      setPendingReset(null)
      setNewPassword('')
      setConfirmPassword('')
      if (saveResult.mode === 'local') {
        toast('A senha foi salva apenas neste dispositivo. O acesso foi liberado.')
      }
      finishLogin(completedSession)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      console.error('[APP DNA] Erro ao redefinir senha:', message, error)
      toast.error(`Erro ao redefinir senha: ${message}`)
    } finally {
      setSavingNewPassword(false)
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#0b2f7b] px-4 py-8">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25"
        style={{ backgroundImage: `url(${LOGIN_BACKGROUND_URL})` }}
      />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-7 shadow-[0_30px_70px_rgba(0,0,0,0.35)] sm:p-9">
        <div className="mx-auto flex h-20 w-44 items-center justify-center">
          <img
            src="https://static.lumi.new/material/e4/e4b79c90058e10d6a10b9828a69f2957.jpeg"
            alt="Logo DNA Survey"
            className="max-h-full w-auto object-contain"
          />
        </div>

        <h2 className="mt-6 text-center text-lg font-semibold text-[#0b2f7b]">Acesso ao sistema</h2>
        <p className="mt-2 text-center text-sm text-slate-600">Entre com suas credenciais para continuar.</p>

        <form
          onSubmit={(event) => {
            event.preventDefault()
            handleLogin()
          }}
          className="mt-6 space-y-4"
        >
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">E-mail</label>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-[#0b2f7b] focus:ring-2 focus:ring-[#0b2f7b]/20"
              placeholder="seu@email.com"
              autoComplete="email"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Senha</label>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-[#0b2f7b] focus:ring-2 focus:ring-[#0b2f7b]/20"
              placeholder="Digite sua senha"
              autoComplete="current-password"
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(event) => setRememberDevice(event.target.checked)}
              className="h-3.5 w-3.5 rounded accent-[#0b2f7b]"
            />
            Lembrar meu e-mail neste dispositivo
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-[#0b2f7b] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#09255f] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>

          {savedAccess?.email && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5">
              <p className="text-sm font-semibold text-[#0b2f7b]">Acesso salvo neste dispositivo</p>
              <p className="mt-1 text-xs leading-5 text-slate-600">
                {savedAccess.userName ? `${savedAccess.userName} | ` : ''}
                {savedAccess.email}
              </p>
            </div>
          )}
        </form>
      </div>

      <p className="relative z-10 mt-6 text-center text-xs text-white/70">
        R3 © 2025 - DNA Pesquisas - v {APP_VERSION} -{' '}
        <a
          href={SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-white underline-offset-2 transition hover:underline"
        >
          Solicitar suporte
        </a>
      </p>

      {pendingReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#0b2f7b]/8 text-[#0b2f7b]">
                <KeyRound size={20} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Definir nova senha</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  A senha padrão precisa ser trocada antes de entrar.
                </p>
              </div>
            </div>

            <form onSubmit={handleCompletePasswordReset} className="mt-5 space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Nova senha</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-3 text-sm outline-none transition focus:border-[#0b2f7b] focus:ring-2 focus:ring-[#0b2f7b]/15"
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Confirmar senha</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-3 text-sm outline-none transition focus:border-[#0b2f7b] focus:ring-2 focus:ring-[#0b2f7b]/15"
                  autoComplete="new-password"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setPendingReset(null)}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingNewPassword}
                  className="rounded-xl bg-[#0b2f7b] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#09255f] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {savingNewPassword ? 'Salvando...' : 'Salvar senha'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default LoginPage